import React, {useEffect, useState, useCallback, useMemo} from "react";
import {useSBLDashboard} from "./context/SBLDashboardContext";
import {FormattedMessage} from "react-intl";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import downloadSmallTokens from "@amzn/meridian-tokens/base/icon/download-small";
import Select, {SelectOption} from "@amzn/meridian/select";
import Checkbox from "@amzn/meridian/checkbox";
import LoaderView from "../../loader/Loader";
import {
    BLUE_COLOR,
    ORANGE_COLOR,
    LOW_BATTERY_THRESHOLD,
    ANDON_COLORS,
    TIMER_OPTIONS,
    DEFAULT_DURATION,
    LIGHT_COMMAND_ON,
    LIGHT_COMMAND_OFF,
    ASCENDING,
    EMPTY_STRING_PLACEHOLDER,
    SBL_DASHBOARD_ACTIONS,
    BATTERY_COLUMN,
    FLOOR_LOCATION_COLUMN,
    DEFAULT_SORT_COLUMN,
    SBL_FILTER_COLUMNS,
    CART_STATUS_COLUMN,
    CART_STATUS_SORT_ORDER,
    TELEMETRY_TIMESTAMP_COLUMN,
    TELEMETRY_AGE_REFRESH_MILLIS
} from "../../util/Constants";
import {createDownloadableFile, generateFileName, getNodeId, getStatusRefreshInterval} from "../../util/Util";
import RefreshTimer from "../../util/RefreshTimer";
import CartFilterPanel from "../ledcontrollerstag/CartFilterPanel";
import {applyPercentileFilter} from "../ledcontrollerstag/CartFilterUtils";
import {
    getCartStatus,
    getCartStatusColor,
    getTelemetryEpoch,
    formatTelemetryAge,
    formatTelemetryTimestamp,
    getTelemetryTimestampForExport
} from "../../util/CartTelemetryUtils";
import Tooltip from "@amzn/meridian/tooltip";
import {sendOnCommand, sendOffCommand} from "../ledcontrollerstag/SBLCommandUtils";
import {initStationConfig, fetchSBLDashboardData} from "./network/SBLDashboardNetworkAPI";
import SearchFilterBar from "../../commonui/SearchFilterBar";

// Resolves the value a filter matches against. Most columns are plain telemetry attributes, but
// status is derived at read time and so is absent from dynamicAttributes -- filtering on it has to
// go through getCartStatus rather than a key lookup.
const getFilterValue = (item, key, now) => {
    if (key === DEFAULT_SORT_COLUMN) return item.scannableId;
    if (key === CART_STATUS_COLUMN) return getCartStatus(item.dynamicAttributes, now);
    return (item.dynamicAttributes || {})[key];
};

const getBatteryColor = (battery) => {
    const value = parseInt(battery, 10);
    if (isNaN(value)) return ORANGE_COLOR;
    return value < LOW_BATTERY_THRESHOLD ? ORANGE_COLOR : BLUE_COLOR;
};

const SBLDashboard = () => {
    const {viewModel, dispatch} = useSBLDashboard();
    const [isFiltered, setIsFiltered] = useState(false);
    const [selectedCarts, setSelectedCarts] = useState(new Set());
    const [color, setColor] = useState(ANDON_COLORS[0].value);
    const [duration, setDuration] = useState(DEFAULT_DURATION);
    const [sending, setSending] = useState(false);
    const [commandResult, setCommandResult] = useState(null);
    const [searchFilters, setSearchFilters] = useState([{key: DEFAULT_SORT_COLUMN, value: ''}]);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        dispatch({type: SBL_DASHBOARD_ACTIONS.LOAD_VIEW});
        initStationConfig(dispatch);
        fetchSBLDashboardData(dispatch);
    }, [dispatch]);

    // Recompute ages and statuses locally. Cheap, so it runs more frequently (every 1 min) than the refetch below.
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), TELEMETRY_AGE_REFRESH_MILLIS);
        return () => clearInterval(timer);
    }, []);

    // Refetch, so that a page left open does not age carts into a false Offline on frozen data.
    // Same cadence and hidden-tab guard as the other overview tabs use for connectivity.
    useEffect(() => {
        const statusInterval = setInterval(() => {
            if (!document.hidden) {
                fetchSBLDashboardData(dispatch);
                setNow(Date.now());
            }
        }, getStatusRefreshInterval());
        return () => clearInterval(statusInterval);
    }, [dispatch]);

    const onSort = useCallback((sortDetails) => {
        dispatch({type: SBL_DASHBOARD_ACTIONS.SORT_DATA, data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}});
    }, [dispatch]);

    const {filteredData, filteredIds, allSelected} = useMemo(() => {
        const allData = viewModel.resourceDynamicAttributes || [];
        const displayData = isFiltered ? applyPercentileFilter(allData, viewModel.percentileThreshold) : allData;
        const searchedData = displayData.filter(item => {
            return searchFilters.every(({key, value}) => {
                if (!value) return true;
                const query = value.toLowerCase();
                const itemValue = getFilterValue(item, key, now);
                if (!itemValue) return false;
                const normalizedItem = itemValue.toLowerCase();
                if (normalizedItem.includes(query)) return true;
                if (key === DEFAULT_SORT_COLUMN) {
                    const cartNumber = itemValue.split('-').pop();
                    return cartNumber === query.replace(/^0+/, '') ||
                        cartNumber === query;
                }
                return false;
            });
        });
        const {column, direction} = viewModel.sortPref;
        const sorted = [...searchedData].sort((a, b) => {
            let valA, valB;
            if (column === BATTERY_COLUMN) {
                valA = parseInt((a.dynamicAttributes || {})[BATTERY_COLUMN], 10) || 0;
                valB = parseInt((b.dynamicAttributes || {})[BATTERY_COLUMN], 10) || 0;
            } else if (column === FLOOR_LOCATION_COLUMN) {
                valA = (a.dynamicAttributes || {})[FLOOR_LOCATION_COLUMN] || "";
                valB = (b.dynamicAttributes || {})[FLOOR_LOCATION_COLUMN] || "";
            } else if (column === CART_STATUS_COLUMN) {
                valA = CART_STATUS_SORT_ORDER[getCartStatus(a.dynamicAttributes, now)];
                valB = CART_STATUS_SORT_ORDER[getCartStatus(b.dynamicAttributes, now)];
            } else if (column === TELEMETRY_TIMESTAMP_COLUMN) {
                const epochA = getTelemetryEpoch(a.dynamicAttributes);
                const epochB = getTelemetryEpoch(b.dynamicAttributes);
                if (epochA === null || epochB === null) {
                    if (epochA === null && epochB === null) return 0;
                    return epochA === null ? 1 : -1;
                }
                valA = epochA;
                valB = epochB;
            } else {
                valA = a[column] || "";
                valB = b[column] || "";
            }
            if (valA < valB) return direction === ASCENDING ? -1 : 1;
            if (valA > valB) return direction === ASCENDING ? 1 : -1;
            return 0;
        });
        const ids = sorted.map(d => d.scannableId);
        const allSel = ids.length > 0 && ids.every(id => selectedCarts.has(id));
        return {filteredData: sorted, filteredIds: ids, allSelected: allSel};
    }, [viewModel.resourceDynamicAttributes, viewModel.percentileThreshold, viewModel.sortPref, isFiltered, selectedCarts, searchFilters, now]);

    const handleSendOn = useCallback(async () => {
        const ids = Array.from(selectedCarts);
        if (ids.length === 0) return;
        setSending(true);
        setCommandResult(null);
        try {
            const result = await sendOnCommand(ids, color, duration);
            setCommandResult({command: LIGHT_COMMAND_ON, ...result});
        } catch (e) {
            setCommandResult({command: LIGHT_COMMAND_ON, succeeded: 0, failed: ids.length, total: ids.length});
        } finally {
            setSending(false);
        }
    }, [selectedCarts, color, duration]);

    const handleSendOff = useCallback(async () => {
        const ids = Array.from(selectedCarts);
        if (ids.length === 0) return;
        setSending(true);
        setCommandResult(null);
        try {
            const result = await sendOffCommand(ids);
            setCommandResult({command: LIGHT_COMMAND_OFF, ...result});
        } catch (e) {
            setCommandResult({command: LIGHT_COMMAND_OFF, succeeded: 0, failed: ids.length, total: ids.length});
        } finally {
            setSending(false);
        }
    }, [selectedCarts]);

    const toggleCartSelection = useCallback((scannableId) => {
        setSelectedCarts(prev => {
            const next = new Set(prev);
            if (next.has(scannableId)) next.delete(scannableId);
            else next.add(scannableId);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback((ids) => {
        setSelectedCarts(prev => {
            const allSel = ids.every(id => prev.has(id));
            if (allSel) return new Set();
            return new Set(ids);
        });
    }, []);

    const handleDownload = useCallback(() => {
        const headers = ['Cart ID', 'Status', 'Last Reported (UTC)', 'Battery (%)', 'Location',
            'Dev EUI', 'Version', 'Is Charging'];
        const rows = filteredData.map(item => {
            const attrs = item.dynamicAttributes || {};
            return [
                item.scannableId,
                getCartStatus(attrs, now),
                getTelemetryTimestampForExport(attrs),
                attrs[BATTERY_COLUMN] || '',
                attrs[FLOOR_LOCATION_COLUMN] || '',
                attrs.dev_eui || '',
                attrs.version || '',
                attrs.is_charging || ''
            ].join(',');
        });
        const csv = [headers.join(','), ...rows].join('\n');
        createDownloadableFile(generateFileName('SBL_Dashboard_' + (getNodeId() || '')), csv);
    }, [filteredData, now]);

    const allData = viewModel.resourceDynamicAttributes || [];
    const percentile = viewModel.percentileThreshold;

    return (
        <Row widths="100%" alignmentVertical="top" spacingInset="none none large none">
            <Column spacing="medium">
                {viewModel.loading ? <LoaderView/> : (
                    <React.Fragment>
                        {viewModel.error && (
                            <Alert type="error" size="small">
                                {viewModel.error}
                            </Alert>
                        )}
                        <RefreshTimer deps={[viewModel.resourceDynamicAttributes]}/>
                        <CartFilterPanel
                            percentileThreshold={percentile}
                            isFiltered={isFiltered}
                            onToggleFilter={() => setIsFiltered(prev => !prev)}
                            matchCount={filteredData.length}
                            totalCount={allData.length}
                        />
                        <SearchFilterBar
                            filters={searchFilters}
                            onFiltersChange={setSearchFilters}
                            filterColumns={SBL_FILTER_COLUMNS}
                        />

                        {commandResult && (
                            <Alert
                                type={commandResult.failed === 0 ? "success" : "warning"}
                                size="small"
                                onClose={() => setCommandResult(null)}
                            >
                                {commandResult.command} command: {commandResult.succeeded}/{commandResult.total} succeeded
                                {commandResult.failed > 0 && `, ${commandResult.failed} failed`}
                            </Alert>
                        )}

                        <Row alignmentVertical="bottom" alignmentHorizontal="justify" spacing="small">
                            <Row alignmentVertical="bottom" spacing="small">
                                <Column spacing="xsmall">
                                    <Text type="b200">Color</Text>
                                    <Select value={color} onChange={setColor} size="small">
                                        {ANDON_COLORS.map(c => (
                                            <SelectOption key={c.value} value={c.value} label={c.label}/>
                                        ))}
                                    </Select>
                                </Column>
                                <Column spacing="xsmall">
                                    <Text type="b200">Timer</Text>
                                    <Select value={duration} onChange={setDuration} size="small">
                                        {TIMER_OPTIONS.map(t => (
                                            <SelectOption key={t.value} value={t.value} label={t.label}/>
                                        ))}
                                    </Select>
                                </Column>
                                <Button type="primary" size="small" onClick={handleSendOn} disabled={sending || selectedCarts.size === 0}>
                                    Send ON ({selectedCarts.size})
                                </Button>
                                <Button type="secondary" size="small" onClick={handleSendOff} disabled={sending || selectedCarts.size === 0}>
                                    Send OFF ({selectedCarts.size})
                                </Button>
                                {selectedCarts.size > 0 && (
                                    <Button type="tertiary" size="small" onClick={() => setSelectedCarts(new Set())}>
                                        Clear Selection
                                    </Button>
                                )}
                            </Row>
                            <Button type="tertiary" size="small" onClick={handleDownload} disabled={filteredData.length === 0}>
                                <Icon tokens={downloadSmallTokens}/>
                                Download CSV
                            </Button>
                        </Row>

                        {filteredData.length === 0 ?
                            <Alert type="informational" size="small">
                                <FormattedMessage id="sbl.dashboard.nodata" defaultMessage="No data available."/>
                            </Alert> :
                            <Table
                                headerRows={1}
                                spacing="small"
                                sortColumn={viewModel.sortPref.column}
                                sortDirection={viewModel.sortPref.direction}
                                onSort={onSort}
                                showDividers={true}
                            >
                                <TableRow highlightOnHover={false}>
                                    <TableCell>
                                        <Checkbox
                                            checked={allSelected}
                                            onChange={() => toggleSelectAll(filteredIds)}
                                        />
                                    </TableCell>
                                    <TableCell sortColumn="scannableId">Cart ID</TableCell>
                                    <TableCell sortColumn={CART_STATUS_COLUMN}>Status</TableCell>
                                    <TableCell sortColumn={TELEMETRY_TIMESTAMP_COLUMN}>Last Reported</TableCell>
                                    <TableCell sortColumn={BATTERY_COLUMN}>Battery (%)</TableCell>
                                    <TableCell sortColumn={FLOOR_LOCATION_COLUMN}>Location</TableCell>
                                    <TableCell>Dev EUI</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Is Charging</TableCell>
                                </TableRow>
                                {filteredData.map((item) => {
                                    const attrs = item.dynamicAttributes || {};
                                    const batteryColor = getBatteryColor(attrs[BATTERY_COLUMN]);
                                    const cartStatus = getCartStatus(attrs, now);
                                    return (
                                        <TableRow key={item.scannableId} highlightOnHover={true}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedCarts.has(item.scannableId)}
                                                    onChange={() => toggleCartSelection(item.scannableId)}
                                                />
                                            </TableCell>
                                            <TableCell><b>{item.scannableId}</b></TableCell>
                                            <TableCell>
                                                <b style={{color: getCartStatusColor(cartStatus)}}>
                                                    {cartStatus}
                                                </b>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip position="top" title={formatTelemetryTimestamp(attrs)}>
                                                    <span>{formatTelemetryAge(attrs, now)}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <b style={{color: batteryColor}}>
                                                    {attrs[BATTERY_COLUMN] || EMPTY_STRING_PLACEHOLDER}
                                                </b>
                                            </TableCell>
                                            <TableCell>{attrs[FLOOR_LOCATION_COLUMN] || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                            <TableCell>{attrs.dev_eui || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                            <TableCell>{attrs.version || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                            <TableCell>{attrs.is_charging || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        }
                    </React.Fragment>
                )}
            </Column>
        </Row>
    );
};

export default SBLDashboard;



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/SBLDashboard.js